import { Controller } from 'stimulus';
import { post, destroy } from '@rails/request.js';

/* global YT */

export default class extends Controller {
  static targets = ['videoContainer', 'videoTitle', 'video'];
  static values = {
    isDesigner: Boolean,
    videoViewsApiPath: String,
    analyticsApiPath: String,
    navigateOnAdvance: Boolean,
    autoplay: Boolean,
  };

  #dialogElement;
  #dialogOpenObserver;
  #boundPauseOnClose;
  #boundPersistOnUnload;
  #currentVideo;

  connect() {
    this.player = null;
    this.#boundPauseOnClose = () => {
      this.persistWatchTime();
      this.player?.pauseVideo();
    };
    this.#dialogElement = this.element.closest('dialog');
    this.#dialogElement?.addEventListener('close', this.#boundPauseOnClose);
    this.#boundPersistOnUnload = () => {
      if (this.#dialogElement?.open) {
        this.persistWatchTime();
      }
    };
    window.addEventListener('beforeunload', this.#boundPersistOnUnload);

    // When mounted inside a closed <dialog>, the container has zero layout
    // dimensions. Initialising the YT iframe at that point makes YouTube serve
    // its lowest-resolution poster (default.jpg), which then stays for the
    // session. Defer initialisation until the dialog is opened.
    if (this.#dialogElement && !this.#dialogElement.open) {
      this.#dialogOpenObserver = new MutationObserver(() => {
        if (this.#dialogElement.open) {
          this.#dialogOpenObserver.disconnect();
          this.#dialogOpenObserver = null;
          this.#loadYouTubeAPI();
        }
      });
      this.#dialogOpenObserver.observe(this.#dialogElement, {
        attributes: true,
        attributeFilter: ['open'],
      });
    } else {
      this.#loadYouTubeAPI();
    }
  }

  disconnect() {
    this.#dialogOpenObserver?.disconnect();
    this.#dialogOpenObserver = null;
    this.#dialogElement?.removeEventListener('close', this.#boundPauseOnClose);
    window.removeEventListener('beforeunload', this.#boundPersistOnUnload);
    this.player?.destroy();
    this.player = null;
  }

  selectVideo(event) {
    event.preventDefault();
    this.videoContainerTarget.scrollIntoView({ behavior: 'smooth' });
    this.#playVideo(event.currentTarget, { autoplay: true });
  }

  resume() {
    this.player?.playVideo();
  }

  playVideoBySlug(slug) {
    const videoEl = this.videoTargets.find((el) => el.dataset.videoSlug === slug);

    if (!videoEl) {
      return;
    }

    if (window.YT?.Player) {
      this.#playVideo(videoEl, { autoplay: true });
      return;
    }

    this.videoTargets.forEach((el) => {
      el.dataset.playing = el === videoEl;
      el.classList.toggle('video-subcategories__video--playing', el === videoEl);
    });
    this.videoTitleTarget.textContent = videoEl.dataset.name;
  }

  async toggleWatched(event) {
    event.preventDefault();
    event.stopPropagation();

    const videoEl = event.target.closest('[data-video-library-target="video"]');
    if (!videoEl) {
      return;
    }

    const isCompleted = videoEl.dataset.completed === 'true';

    if (isCompleted) {
      const response = await destroy(videoEl.dataset.videoViewsApiPath);
      await this.#handleVideoViewResponse(response, videoEl, false);
    } else {
      const duration = Number(videoEl.dataset.duration);
      const response = await post(this.videoViewsApiPathValue, {
        body: JSON.stringify({
          video_view: { video_id: videoEl.dataset.videoId, time_watched: duration },
        }),
      });
      await this.#handleVideoViewResponse(response, videoEl, true);
    }
  }

  #loadYouTubeAPI() {
    if (window.YT?.Player) {
      this.#onAPIReady();
      return;
    }

    // If the script is already being loaded by another instance, just wait for the callback.
    if (document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const existingCallback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        existingCallback?.();
        this.#onAPIReady();
      };
      return;
    }

    window.onYouTubeIframeAPIReady = () => this.#onAPIReady();

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(script);
  }

  #onAPIReady() {
    this.#currentVideo =
      this.videoTargets.find((el) => el.dataset.playing === 'true') || this.videoTargets[0];

    if (this.#currentVideo) {
      this.#initPlayer(this.#currentVideo, { autoplay: this.autoplayValue });
    }
  }

  #playVideo(videoEl, { autoplay = false } = {}) {
    // Persist watch progress on the previous video before destroying its player
    this.persistWatchTime();
    this.player?.destroy();
    this.player = null;

    this.videoTitleTarget.textContent = videoEl.dataset.name;

    this.#currentVideo?.classList.remove('video-subcategories__video--playing');
    videoEl.classList.add('video-subcategories__video--playing');
    this.#currentVideo = videoEl;

    this.#initPlayer(videoEl, { autoplay });
  }

  #initPlayer(videoEl, { autoplay = false } = {}) {
    const youtubeId = this.#extractYouTubeId(videoEl.dataset.embedUrl);
    if (!youtubeId) {
      return;
    }

    this.#resetVideoContainer();

    this.player = new YT.Player('video-library-modal-yt-player', {
      videoId: youtubeId,
      width: '100%',
      height: '100%',
      playerVars: {
        autoplay: autoplay ? 1 : 0,
        origin: window.location.origin,
        start: Number(videoEl.dataset.startSeconds) || 0,
        rel: 0,
      },
      events: {
        onStateChange: (event) => this.#onPlayerStateChange(event),
      },
    });
  }

  #onPlayerStateChange(event) {
    if (event.data === YT.PlayerState.PLAYING && !event.target.hasTrackedInitialPlay) {
      event.target.hasTrackedInitialPlay = true;
      this.#trackVideoLessonWatched();
    }

    if (event.data === YT.PlayerState.ENDED) {
      this.persistWatchTime({ completed: true });
      this.#advanceToNextVideo();
    }
  }

  async #trackVideoLessonWatched() {
    if (!this.isDesignerValue || !this.#currentVideo) {
      return;
    }

    const { categorySlug, videoSlug } = this.#currentVideo.dataset;

    const response = await post(this.analyticsApiPathValue, {
      body: JSON.stringify({
        event: {
          name: 'Video Lesson Watched',
          properties: {
            course_title: categorySlug,
            location: window.location.href,
            video_title: videoSlug,
          },
        },
      }),
    });

    if (!response.ok) {
      console.error('Failed to track video:', response.statusCode);
    }
  }

  #advanceToNextVideo() {
    const currentIndex = this.videoTargets.indexOf(this.#currentVideo);
    const nextVideo = this.videoTargets[currentIndex + 1];

    if (!nextVideo) {
      return;
    }

    if (this.navigateOnAdvanceValue) {
      window.location.href = nextVideo.href;
    } else {
      this.#playVideo(nextVideo, { autoplay: true });
    }
  }

  async #handleVideoViewResponse(response, videoEl, completed) {
    if (response.ok) {
      this.#setVideoState(videoEl, completed);
    } else {
      console.error('Failed to update video view', response.statusCode, await response.text);
    }
  }

  #setVideoState(videoEl, completed) {
    videoEl.dataset.completed = completed;
    videoEl.classList.toggle('video-subcategories__video--completed', completed);
    videoEl.classList.toggle('video-subcategories__video--unwatched', !completed);
    this.#updateWatchedTooltip(videoEl, completed);
    this.element.dispatchEvent(new CustomEvent('video-library:status-changed', { bubbles: true }));
  }

  #updateWatchedTooltip(videoEl, completed) {
    const newText = completed ? 'Mark as Incomplete' : 'Mark as Complete';
    const contentEl = videoEl.querySelector('.tooltip__content');
    if (contentEl) {
      contentEl.textContent = newText;
    }
    const wrapperEl = videoEl.querySelector('.video-subcategories__video-icon-wrapper');
    wrapperEl?._tippy?.setContent(newText);
  }

  persistWatchTime({ completed = false } = {}) {
    if (!this.isDesignerValue || !this.#currentVideo) {
      return;
    }

    const videoEl = this.#currentVideo;

    let timeWatched;

    if (completed) {
      timeWatched = Number(videoEl.dataset.duration);
      this.#setVideoState(videoEl, true);
    } else {
      if (!this.player) {
        return;
      }

      let currentTime;
      try {
        currentTime = this.player.getCurrentTime();
      } catch {
        return;
      }

      if (!currentTime || currentTime <= 0) {
        return;
      }

      timeWatched = Math.round(currentTime);
    }

    post(this.videoViewsApiPathValue, {
      body: JSON.stringify({
        video_view: { video_id: videoEl.dataset.videoId, time_watched: timeWatched },
      }),
      keepalive: true,
    });

    videoEl.dataset.startSeconds = completed ? 0 : timeWatched;
  }

  #extractYouTubeId(embedUrl) {
    try {
      return new URL(embedUrl).pathname.split('/').at(-1);
    } catch {
      return null;
    }
  }

  #resetVideoContainer() {
    const playerEl = document.createElement('div');
    playerEl.id = 'video-library-modal-yt-player';
    this.videoContainerTarget.replaceChildren(playerEl);
  }
}
